import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogPanel, Transition, TransitionChild, Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { FiSearch, FiClipboard, FiUser, FiCornerDownLeft, FiFileText, FiBookOpen } from 'react-icons/fi';
import { TzamunAiMark } from '../TzamunAiBrand';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import StatusBadge from './StatusBadge';

/**
 * Global command palette (Ctrl/Cmd+K): jump to pages, find job cards by
 * number/plate, find customers by name/phone. Pure navigation — no writes.
 */
export default function CommandPalette({ isOpen, onClose, menuItems = [], isStaff = false }) {
  const [query, setQuery] = useState('');
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const timer = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setJobs([]);
      setCustomers([]);
      setInvoices([]);
      setQuotations([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setJobs([]);
      setCustomers([]);
      setInvoices([]);
      setQuotations([]);
      return;
    }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const [jobsRes, custRes, invRes, quotRes] = await Promise.allSettled([
          // Explicit limit: the server's LIMIT counts JOINED rows (a card with
          // several assignees eats several), so ask for comfortably more than the
          // 6 shown below or a busy card can starve the list. Never rely on the
          // server default — that is what silently truncated the job-cards board.
          axios.get('/api/job-cards', { params: { search: query.trim(), limit: 30 } }),
          isStaff
            ? Promise.resolve({ data: { data: [] } })
            : axios.get(`/api/customers/search?q=${encodeURIComponent(query.trim())}&type=customer`),
          isStaff
            ? Promise.resolve({ data: { data: [] } })
            : axios.get('/api/invoices', { params: { search: query.trim() } }),
          isStaff
            ? Promise.resolve({ data: { data: [] } })
            : axios.get('/api/quotations', { params: { search: query.trim() } }),
        ]);
        setJobs(jobsRes.status === 'fulfilled' ? (jobsRes.value.data.data || []).slice(0, 6) : []);
        setCustomers(custRes.status === 'fulfilled' ? (custRes.value.data.data || []).slice(0, 4) : []);
        setInvoices(invRes.status === 'fulfilled' ? (invRes.value.data.data || []).slice(0, 4) : []);
        setQuotations(quotRes.status === 'fulfilled' ? (quotRes.value.data.data || []).slice(0, 4) : []);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const filteredPages = query.trim()
    ? menuItems.filter((m) => t(m.i18nKey).toLowerCase().includes(query.trim().toLowerCase()))
    : menuItems.slice(0, 6);

  // "فتح كرت عمل" finds nothing, because it is a QUESTION, not a record — the
  // palette searches job numbers, plates, names and reference numbers. Two
  // answers for that case, offered as ordinary options rather than a dead-end
  // "no results": ask the assistant, or search the manual.
  //
  // Both are gated on menuItems, which arrives already filtered by feature flag
  // and permission — so a tenant without the assistant never sees the option,
  // and neither does a role that cannot use it.
  const trimmed = query.trim();
  const canAsk = menuItems.some((m) => m.path === '/assistant');
  const canManual = menuItems.some((m) => m.path === '/manual');
  const showFallbacks = trimmed.length >= 2 && (canAsk || canManual);

  const handleSelect = (item) => {
    if (!item) return;
    onClose();
    if (item.kind === 'page') navigate(item.path);
    if (item.kind === 'job') navigate(isStaff ? `/jobs/${item.id}` : `/job-cards/${item.id}`);
    if (item.kind === 'customer') navigate('/customers');
    if (item.kind === 'invoice') navigate('/invoices/' + item.id);
    if (item.kind === 'quotation') navigate('/quotations/' + item.id);
    // The assistant runs the prompt on arrival (it already reads state.prompt);
    // the manual applies it as a filter. Neither makes the user retype.
    if (item.kind === 'ask') navigate('/assistant', { state: { prompt: item.q } });
    if (item.kind === 'manual') navigate('/manual', { state: { q: item.q } });
  };

  return (
    <Transition show={!!isOpen} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6 md:p-20 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-6 md:pt-20">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="mx-auto max-w-xl bg-surface border border-edge rounded-2xl shadow-2xl overflow-hidden">
              <Combobox onChange={handleSelect}>
                <div className="relative">
                  <FiSearch className="pointer-events-none absolute top-3.5 start-4 text-content-muted" size={20} />
                  <ComboboxInput
                    autoFocus
                    className="h-12 w-full border-0 bg-transparent ps-12 pe-4 text-content placeholder:text-content-muted focus:outline-none focus:ring-0 text-sm"
                    placeholder={t('commandPlaceholder')}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>

                <ComboboxOptions static className="max-h-96 overflow-y-auto border-t border-edge p-2">
                  {/* Pages */}
                  {filteredPages.length > 0 && (
                    <li className="list-none">
                      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-content-muted">
                        {t('navMenu')}
                      </p>
                      {filteredPages.map((m) => {
                        const Icon = m.icon;
                        return (
                          <ComboboxOption
                            key={m.path}
                            value={{ kind: 'page', path: m.path }}
                            className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-content data-[focus]:bg-accent-soft data-[focus]:text-accent"
                          >
                            <Icon size={16} className="text-content-muted group-data-[focus]:text-accent" />
                            {t(m.i18nKey)}
                            <FiCornerDownLeft size={13} className="ms-auto opacity-0 group-data-[focus]:opacity-60" />
                          </ComboboxOption>
                        );
                      })}
                    </li>
                  )}

                  {/* Job cards */}
                  {jobs.length > 0 && (
                    <li className="list-none">
                      <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-content-muted">
                        {t('jobCards')}
                      </p>
                      {jobs.map((job) => (
                        <ComboboxOption
                          key={job.id}
                          value={{ kind: 'job', id: job.id }}
                          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm data-[focus]:bg-accent-soft"
                        >
                          <FiClipboard size={16} className="text-content-muted group-data-[focus]:text-accent shrink-0" />
                          <span className="font-semibold text-content tabular-nums shrink-0">{job.job_number}</span>
                          <span className="text-content-secondary truncate min-w-0">
                            {job.vehicle?.plate_number} · {job.vehicle?.make} {job.vehicle?.model}
                          </span>
                          <span className="ms-auto shrink-0">
                            <StatusBadge type="job" status={job.status} size="sm" showIcon={false} />
                          </span>
                        </ComboboxOption>
                      ))}
                    </li>
                  )}

                  {/* Customers */}
                  {customers.length > 0 && (
                    <li className="list-none">
                      <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-content-muted">
                        {t('customers')}
                      </p>
                      {customers.map((c) => (
                        <ComboboxOption
                          key={c.id}
                          value={{ kind: 'customer', id: c.id }}
                          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm data-[focus]:bg-accent-soft"
                        >
                          <FiUser size={16} className="text-content-muted group-data-[focus]:text-accent shrink-0" />
                          <span className="font-medium text-content min-w-0 truncate">
                            {c.first_name} {c.last_name}
                          </span>
                          <span className="text-content-muted tabular-nums ms-auto shrink-0" dir="ltr">
                            {c.phone}
                          </span>
                        </ComboboxOption>
                      ))}
                    </li>
                  )}

                  {/* Invoices */}
                  {invoices.length > 0 && (
                    <li className="list-none">
                      <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-content-muted">
                        {t('invoices')}
                      </p>
                      {invoices.map((invoice) => (
                        <ComboboxOption
                          key={invoice.id}
                          value={{ kind: 'invoice', id: invoice.id }}
                          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm data-[focus]:bg-accent-soft"
                        >
                          <FiFileText size={16} className="text-content-muted group-data-[focus]:text-accent shrink-0" />
                          <span className="font-semibold text-content tabular-nums shrink-0">
                            {invoice.invoice_number || invoice.reference_number}
                          </span>
                          <span className="ms-auto shrink-0">
                            <StatusBadge type="invoice" status={invoice.payment_status} size="sm" showIcon={false} />
                          </span>
                        </ComboboxOption>
                      ))}
                    </li>
                  )}

                  {/* Quotations */}
                  {quotations.length > 0 && (
                    <li className="list-none">
                      <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-content-muted">
                        {t('quotations')}
                      </p>
                      {quotations.map((quotation) => (
                        <ComboboxOption
                          key={quotation.id}
                          value={{ kind: 'quotation', id: quotation.id }}
                          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm data-[focus]:bg-accent-soft"
                        >
                          <FiFileText size={16} className="text-content-muted group-data-[focus]:text-accent shrink-0" />
                          <span className="font-semibold text-content tabular-nums shrink-0">
                            {quotation.quotation_number}
                          </span>
                          <span className="ms-auto shrink-0">
                            <StatusBadge type="quotation" status={quotation.status} size="sm" showIcon={false} />
                          </span>
                        </ComboboxOption>
                      ))}
                    </li>
                  )}

                  {query.trim().length >= 2 && !loading && jobs.length === 0 && customers.length === 0 && invoices.length === 0 && quotations.length === 0 && filteredPages.length === 0 && (
                    <p className="px-3 pt-6 pb-2 text-center text-sm text-content-muted">{t('noResultsFound')}</p>
                  )}

                  {/* Always offered while typing, not only on an empty result:
                    a question can also be typed while records DO match, and
                    hiding the option until the search fails is a worse place to
                    discover it. */}
                  {showFallbacks && (
                    <li className="list-none">
                      <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-content-muted">
                        {t('cmd.needHelp')}
                      </p>
                      {canAsk && (
                        <ComboboxOption
                          value={{ kind: 'ask', q: trimmed }}
                          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm data-[focus]:bg-accent-soft"
                        >
                          <TzamunAiMark className="h-4 shrink-0" />
                          <span className="text-content truncate min-w-0">
                            {t('cmd.askAi', { query: trimmed })}
                          </span>
                          <FiCornerDownLeft size={13} className="ms-auto shrink-0 opacity-0 group-data-[focus]:opacity-60" />
                        </ComboboxOption>
                      )}
                      {canManual && (
                        <ComboboxOption
                          value={{ kind: 'manual', q: trimmed }}
                          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm data-[focus]:bg-accent-soft"
                        >
                          <FiBookOpen size={16} className="text-content-muted group-data-[focus]:text-accent shrink-0" />
                          <span className="text-content truncate min-w-0">
                            {t('cmd.searchManual', { query: trimmed })}
                          </span>
                          <FiCornerDownLeft size={13} className="ms-auto shrink-0 opacity-0 group-data-[focus]:opacity-60" />
                        </ComboboxOption>
                      )}
                    </li>
                  )}
                  {loading && (
                    <p className="px-3 py-3 text-center text-xs text-content-muted animate-pulse">{t('searching')}…</p>
                  )}
                </ComboboxOptions>
              </Combobox>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
